import React, { useEffect, useRef, useState } from "react";
import { Box, Spinner, Heading } from "@chakra-ui/react";
import axios from "axios";
import echarts from "./lib";

// The brand chart ramp from theme/tokens.js (teal / blue / green / amber
// families), each step brightened so the gradient treatment glows while
// every hue stays on-brand.
const colorPalette = [
  "#14A3B2", // teal        (brand 0E7C86, brightened)
  "#E8940F", // amber       (brand A8690A, brightened)
  "#3B8FD0", // blue        (brand 2C6E9B, brightened)
  "#1AA47C", // green       (brand 12775F, brightened)
  "#58B6C0", // light teal  (brand.300, as-is)
  "#E9B84C", // light amber (brand D6A75B, brightened)
  "#7CB6E4", // light blue  (brand 7FA8C4, brightened)
  "#6FC9A3", // light green (brand 63A98C, brightened)
];

const fmtShort = (value) => {
  if (value >= 1000000) return `R${(value / 1000000).toFixed(1)}M`;
  if (value >= 1000) return `R${(value / 1000).toFixed(0)}K`;
  return `R${value.toLocaleString("en-US")}`;
};

// Mix a hex colour toward white — the inner edge of each gradient slice.
const lighten = (hex, amt) => {
  const n = parseInt(hex.slice(1), 16);
  const ch = (shift) => {
    const c = (n >> shift) & 0xff;
    return Math.round(c + (255 - c) * amt);
  };
  return `rgb(${ch(16)}, ${ch(8)}, ${ch(0)})`;
};

const PIE_CENTER_X = 0.34; // pie sits left of the legend
const PIE_OUTER = 0.82; // outer radius as fraction of half-height

// Per-slice label ink: dark text on light slices, white on dark ones,
// so the in-slice percentages stay readable on every colour.
const labelInk = (hex) => {
  const n = parseInt(hex.slice(1), 16);
  const lum =
    (0.299 * ((n >> 16) & 0xff) + 0.587 * ((n >> 8) & 0xff) + 0.114 * (n & 0xff)) / 255;
  return lum > 0.55 ? "#1D2F3A" : "#ffffff";
};

const Chart1 = ({ supplier, retailer }) => {
  const containerRef = useRef(null);
  const chartRef = useRef(null);
  const refreshGradientsRef = useRef(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let disposed = false;

    const initChart = async () => {
      if (!containerRef.current) return;
      setLoading(true);
      try {
        const res = await axios.post(
          `${process.env.REACT_APP_API_URL}/rep_sales/chart1`,
          {
            supplier,
            retailer,
            userid: localStorage.getItem("userid"),
          }
        );

        // Identical transform to the AmCharts version — the DATA must not
        // change in this migration; only the presentation does.
        const data = res.data.data.map((item) => ({
          name: item.site,
          value: Math.round(item.value),
        }));

        if (disposed || !containerRef.current) return;

        const fullTotal = data.reduce((sum, d) => sum + d.value, 0);
        const pct = (v) => (fullTotal ? ((100 * v) / fullTotal).toFixed(1) : "0.0");

        const chart = echarts.init(containerRef.current);
        chartRef.current = chart;

        // ApexCharts-style sheen: one radial gradient across the whole ring
        // (lighter at the inner edge, full colour at the rim). Gradients are
        // in pixel coordinates, so they're rebuilt on every resize.
        const gradientData = () => {
          const w = chart.getWidth();
          const h = chart.getHeight();
          const cx = PIE_CENTER_X * w;
          const cy = 0.5 * h;
          const r = (PIE_OUTER * Math.min(w, h)) / 2;
          return data.map((d, i) => {
            const base = colorPalette[i % colorPalette.length];
            const ink = labelInk(base);
            return {
              ...d,
              label: {
                color: ink,
                ...(ink === "#ffffff"
                  ? { textShadowColor: "rgba(0,0,0,0.28)", textShadowBlur: 3 }
                  : {}),
              },
              itemStyle: {
                color: {
                  type: "radial",
                  x: cx,
                  y: cy,
                  r,
                  global: true,
                  colorStops: [
                    { offset: 0.56, color: lighten(base, 0.42) },
                    { offset: 1, color: base },
                  ],
                },
              },
            };
          });
        };
        refreshGradientsRef.current = () =>
          chart.setOption({ series: [{ data: gradientData() }] });

        const setCenter = (main, sub, color) =>
          chart.setOption({
            title: {
              text: main,
              subtext: sub,
              textStyle: { color: color || "#1D2F3A" },
            },
          });

        chart.setOption({
          color: colorPalette, // legend markers keep the flat base colour
          title: {
            text: fmtShort(fullTotal),
            subtext: "total sales",
            left: "34%",
            top: "39%",
            textAlign: "center",
            textStyle: { color: "#1D2F3A", fontSize: 22, fontWeight: 700 },
            subtextStyle: { color: "#64798A", fontSize: 11 },
            itemGap: 2,
          },
          tooltip: {
            confine: true,
            backgroundColor: "#ffffff",
            borderColor: "#E2E9EE",
            borderWidth: 1,
            padding: [8, 12],
            textStyle: { color: "#1D2F3A", fontSize: 12 },
            extraCssText: "box-shadow: 0 6px 18px rgba(29,47,58,0.14); border-radius: 8px;",
            formatter: (p) =>
              `${p.marker} ${p.name}<br/><b>R${p.value.toLocaleString("en-US")}</b> — ${pct(p.value)}%`,
          },
          legend: {
            type: "scroll",
            orient: "vertical",
            right: 0,
            top: "middle",
            icon: "circle",
            itemWidth: 10,
            itemHeight: 10,
            itemGap: 8,
            formatter: (name) => {
              const d = data.find((x) => x.name === name);
              return d ? `{name|${name}}{pct| ${pct(d.value)}%}` : name;
            },
            textStyle: {
              rich: {
                name: { color: "#1D2F3A", fontSize: 12, fontWeight: 500 },
                pct: { color: "#64798A", fontSize: 12, padding: [0, 0, 0, 4] },
              },
            },
          },
          series: [
            {
              type: "pie",
              radius: ["46%", "82%"],
              center: [`${PIE_CENTER_X * 100}%`, "50%"],
              data: gradientData(),
              itemStyle: {
                borderRadius: 7,
                borderColor: "#ffffff",
                borderWidth: 2,
              },
              // In-slice percentage labels (the AG Charts look Michael liked);
              // slivers under 4% stay unlabelled — the legend still carries them.
              label: {
                show: true,
                position: "inside",
                formatter: (p) => (Number(pct(p.value)) >= 4 ? `${pct(p.value)}%` : ""),
                fontSize: 12.5,
                fontWeight: 700,
                fontFamily: "Inter, sans-serif",
              },
              labelLine: { show: false },
              emphasis: {
                scale: true,
                scaleSize: 6,
                itemStyle: { shadowBlur: 14, shadowColor: "rgba(29,47,58,0.25)" },
              },
              animationType: "scale",
              animationEasing: "elasticOut",
              animationDuration: 900,
              animationDelay: (idx) => idx * 60,
            },
          ],
        });

        chart.on("mouseover", { seriesIndex: 0 }, (p) => {
          setCenter(fmtShort(p.value), p.name, colorPalette[p.dataIndex % colorPalette.length]);
        });
        chart.on("mouseout", { seriesIndex: 0 }, () => {
          setCenter(fmtShort(fullTotal), "total sales", "#1D2F3A");
        });
        chart.on("legendselectchanged", (e) => {
          const visibleTotal = data.reduce(
            (sum, d) => sum + (e.selected[d.name] === false ? 0 : d.value),
            0
          );
          setCenter(fmtShort(visibleTotal), "total sales", "#1D2F3A");
        });
      } catch (error) {
        console.error("Error fetching data", error);
      } finally {
        if (!disposed) setLoading(false);
      }
    };

    initChart();

    const ro = new ResizeObserver(() => {
      if (chartRef.current) {
        chartRef.current.resize();
        if (refreshGradientsRef.current) refreshGradientsRef.current();
      }
    });
    if (containerRef.current) ro.observe(containerRef.current);

    return () => {
      disposed = true;
      ro.disconnect();
      refreshGradientsRef.current = null;
      if (chartRef.current) {
        chartRef.current.dispose();
        chartRef.current = null;
      }
    };
  }, [supplier, retailer]);

  return (
    <Box height="100%" display="flex" flexDirection="column" position="relative">
      <Heading size="xs" color="gray.600" mb={2} fontWeight="600">
        Sales by Site Profile
      </Heading>
      <Box flex="1" position="relative">
        <div
          ref={containerRef}
          style={{ width: "100%", height: "100%", overflow: "hidden" }}
        ></div>
        {loading && (
          <Spinner
            position="absolute"
            top="50%"
            left="50%"
            transform="translate(-50%, -50%)"
          />
        )}
      </Box>
    </Box>
  );
};

export default Chart1;
